/** Date-range, CSV and formatting helpers for the reporting module. */

export type PeriodPresetKey =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'ytd'
  | 'last_12m'
  | 'custom';

export interface PeriodPreset {
  key: PeriodPresetKey;
  label: string;
}

export const PERIOD_PRESETS: PeriodPreset[] = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'last_12m', label: 'Last 12 months' },
  { key: 'custom', label: 'Custom' },
];

function toDateStr(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Resolve a preset into inclusive YYYY-MM-DD bounds, relative to `today`. */
export function resolvePeriod(
  preset: PeriodPresetKey,
  today = new Date()
): { fromDate: string; toDate: string } {
  const year = today.getFullYear();
  const month = today.getMonth();

  switch (preset) {
    case 'this_month':
      return {
        fromDate: toDateStr(new Date(year, month, 1)),
        toDate: toDateStr(new Date(year, month + 1, 0)),
      };
    case 'last_month':
      return {
        fromDate: toDateStr(new Date(year, month - 1, 1)),
        toDate: toDateStr(new Date(year, month, 0)),
      };
    case 'this_quarter': {
      const quarterStart = Math.floor(month / 3) * 3;
      return {
        fromDate: toDateStr(new Date(year, quarterStart, 1)),
        toDate: toDateStr(new Date(year, quarterStart + 3, 0)),
      };
    }
    case 'ytd':
      return { fromDate: toDateStr(new Date(year, 0, 1)), toDate: toDateStr(today) };
    case 'last_12m':
    case 'custom':
    default:
      return {
        fromDate: toDateStr(new Date(year, month - 11, 1)),
        toDate: toDateStr(new Date(year, month + 1, 0)),
      };
  }
}

/** "2026-03" -> "Mar 2026" */
export function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  if (!year || !month) return monthKey;
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/** "2026-03-14" -> "2026-03" */
export function monthKeyOf(dateStr: string): string {
  return dateStr.slice(0, 7);
}

/** Every YYYY-MM between two dates, inclusive — so a gap month still plots as zero. */
export function monthsBetween(fromDate: string, toDate: string): string[] {
  const [fromYear, fromMonth] = fromDate.split('-').map(Number);
  const [toYear, toMonth] = toDate.split('-').map(Number);
  if (!fromYear || !toYear) return [];
  const months: string[] = [];
  const cursor = new Date(fromYear, fromMonth - 1, 1);
  const end = new Date(toYear, toMonth - 1, 1);
  // Guard against an inverted range producing an unbounded loop.
  while (cursor <= end && months.length < 240) {
    months.push(`${cursor.getFullYear()}-${`${cursor.getMonth() + 1}`.padStart(2, '0')}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

/** Whole days from `fromStr` to `toStr`; negative when `toStr` is in the past. */
export function daysUntil(fromStr: string, toStr: string): number {
  const from = new Date(`${fromStr}T00:00:00`).getTime();
  const to = new Date(`${toStr}T00:00:00`).getTime();
  return Math.round((to - from) / 86_400_000);
}

export function todayStr(): string {
  return toDateStr(new Date());
}

/** "$1.2M" / "$840K" — for axis ticks and other space-constrained labels. */
export function formatCompactCurrency(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(value)}`;
}

/** Two-decimal fixed string for money columns in a bordereau. */
export function toMoneyString(value: number): string {
  return value.toFixed(2);
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * Quote a single CSV field per RFC 4180. Values that begin with a formula
 * trigger are prefixed with a tab so a spreadsheet treats them as text — an
 * insured name like "=Acme" must not execute when the broker opens the file.
 */
export function escapeCsvValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text) && Number.isNaN(Number(text))) text = `\t${text}`;
  if (/["\n\r,]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

/** Serialize a header row plus body rows to CRLF-delimited CSV. */
export function toCsv(
  headers: string[],
  rows: (string | number | null)[][],
  includeBom = false
): string {
  const lines = [headers.map(escapeCsvValue).join(',')];
  rows.forEach(row => lines.push(row.map(escapeCsvValue).join(',')));
  return `${includeBom ? '﻿' : ''}${lines.join('\r\n')}\r\n`;
}

/** Replace anything that would be awkward in a filename across platforms. */
export function sanitizeFileName(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/_{2,}/g, '_');
}

/** Push a generated CSV to the browser as a download. */
export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = sanitizeFileName(fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
